import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { connect } from 'react-redux';

import {
  getPurchasedDigitalDownloadCartItem,
  getPurchasedPhotoPricingPrintProductCartItem,
} from '../../../../../../../../services/checkout/redux/helpers';
import { getCheckoutCart } from '../../../../../../../../services/checkout/redux/checkoutSelectors';
import {
  cartItemUpdate,
  togglePurchasedDigitalDownloadCartItem,
  togglePurchasedPhotoPricingPrintProductCartItem,
} from '../../../../../../../../services/checkout/redux/checkoutActions';
import { openModal as openModalAction } from '../../../../../../../../actions/modal-actions';
import PhotoPricingPrintProductOption from './PhotoPricingPrintProductOption';
import DigitalDownloadOption from './DigitalDownloadOption';

const propTypes = {
  albumMembershipGroup: PropTypes.shape({
    id: PropTypes.string,
  }),
  photoPricing: PropTypes.shape({
    id: PropTypes.string,
  }).isRequired,
  prepaidPhotoPricings: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
      purchased_photo_pricing_print_item: PropTypes.shape({
        id: PropTypes.string,
      }),
      purchased_photo_pricing_print_product_item: PropTypes.shape({
        id: PropTypes.string,
      }),
      quantity: PropTypes.number,
    }),
  ).isRequired,
  cart: PropTypes.array.isRequired,
  onCartItemUpdate: PropTypes.func.isRequired,
  onPurchasedPhotoPricingPrintProductToggle: PropTypes.func.isRequired,
  onPurchasedDigitalDownloadToggle: PropTypes.func.isRequired,
  onCheckout: PropTypes.func.isRequired,
  optionProps: PropTypes.object,
  className: PropTypes.string,
  activeIdentity: PropTypes.shape({
    id: PropTypes.string,
  }),
  albumMembershipPhoto: PropTypes.shape({
    id: PropTypes.string,
  }),
  isAllPhotos: PropTypes.bool,
};

const PhotoPricingOptionList = ({
  albumMembershipGroup,
  prepaidPhotoPricings,
  cart,
  onCartItemUpdate,
  onPurchasedPhotoPricingPrintProductToggle,
  onPurchasedDigitalDownloadToggle,
  onCheckout,
  optionProps,
  className,
  activeIdentity,
  albumMembershipPhoto,
  isAllPhotos,
}) => {
  const classes = classNames({
    'album-print-package-option-list': true,
    [className]: !!className,
  });

  const purchasedDigitalDownloads = useMemo(
    () =>
      prepaidPhotoPricings.filter(
        ({
          photo_pricing_print_item: printItem,
          photo_pricing_print_product_item: printProductItem,
        }) => !printItem && !printProductItem,
      ),
    [prepaidPhotoPricings],
  );

  const purchasedDigitalDownloadsAvailable = useMemo(
    () =>
      purchasedDigitalDownloads.filter(
        ({ id }) =>
          !getPurchasedDigitalDownloadCartItem(cart, {
            purchasedPhotoPricingId: id,
          }),
      ),
    [cart, purchasedDigitalDownloads],
  );

  const purchasedPhotoPricingPrintProductItems = useMemo(
    () =>
      prepaidPhotoPricings.filter(
        ({ photo_pricing_print_product_item: printProductItem }) =>
          !!printProductItem,
      ),
    [prepaidPhotoPricings],
  );

  const digitalDownloadCartItem = (() => {
    let cartItem = null;

    purchasedDigitalDownloads.forEach(purchasedPhotoPricing => {
      if (cartItem) {
        return;
      }

      cartItem = getPurchasedDigitalDownloadCartItem(cart, {
        purchasedPhotoPricingId: purchasedPhotoPricing.id,
        photoId: albumMembershipPhoto?.photo?.id,
      });
    });

    return cartItem;
  })();

  return (
    <div className={classes}>
      {purchasedDigitalDownloads.length > 0 && (
        <DigitalDownloadOption
          albumMembershipPhoto={albumMembershipPhoto}
          cartItem={digitalDownloadCartItem}
          remainingItems={purchasedDigitalDownloadsAvailable.length}
          onDigitalDownloadToggle={({ photo, album }) =>
            onPurchasedDigitalDownloadToggle({
              photo,
              album,
              identity: activeIdentity,
              photoPricing: purchasedDigitalDownloadsAvailable[0].photo_pricing,
              purchasedPhotoPricing: purchasedDigitalDownloadsAvailable[0],
            })
          }
        />
      )}
      {purchasedPhotoPricingPrintProductItems.map(purchasedPhotoPricing => {
        const { quantity, photo_pricing: photoPricing } = purchasedPhotoPricing;

        return (
          <PhotoPricingPrintProductOption
            key={purchasedPhotoPricing.id}
            albumMembershipPhoto={albumMembershipPhoto}
            purchasedPhotoPricing={purchasedPhotoPricing}
            albumMembershipGroup={albumMembershipGroup}
            cartItem={getPurchasedPhotoPricingPrintProductCartItem(cart, {
              purchasedPhotoPricingId: purchasedPhotoPricing.id,
            })}
            onUpdate={onCartItemUpdate}
            onCheckout={onCheckout}
            isAllPhotos={isAllPhotos}
            identity={activeIdentity}
            onPurchasedPhotoPricingPrintProductToggle={({
              accountCard,
              roesConfiguration,
              photoPricingPrintProduct,
              photo,
              album,
            }) => {
              onPurchasedPhotoPricingPrintProductToggle({
                accountCard,
                photoPricingPrintProduct,
                photoPricing,
                purchasedPhotoPricing,
                albumMembershipGroup,
                photo,
                album,
                quantity,
                identity: activeIdentity,
                roesConfiguration,
                isAllPhotos,
              });
            }}
            {...(optionProps ?? {})}
          />
        );
      })}
    </div>
  );
};

PhotoPricingOptionList.propTypes = propTypes;

const mapStateToProps = state => ({
  cart: getCheckoutCart(state),
  activeIdentity: state.checkout.activeIdentity,
});

const mapDispatchToProps = {
  openModal: openModalAction,
  onCartItemUpdate: cartItemUpdate,
  onPurchasedDigitalDownloadToggle: togglePurchasedDigitalDownloadCartItem,
  onPurchasedPhotoPricingPrintProductToggle: togglePurchasedPhotoPricingPrintProductCartItem,
};

export default connect(
  mapStateToProps,
  mapDispatchToProps,
)(PhotoPricingOptionList);
